<template>
  <div
    class="builder-element"
    :style="elementStyles"
    :class="{ 'selected': isSelected, 'drag-over': isDragOver }"
    draggable="true"
    @dragstart="handleDragStart"
    @dragend="handleDragEnd"
    @dragover="handleDragOver"
    @dragleave="handleDragLeave"
    @drop="handleDrop"
    @click.stop="handleSelect"
    @mouseenter="showToolbar = true"
    @mouseleave="showToolbar = false"
  >
    <Transition name="fade">
      <div v-if="showToolbar || isSelected" class="element-toolbar">
        <button type="button" class="toolbar-btn" title="Edit" @click.stop="handleEdit">
          <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor">
            <path d="M12.146.146a.5.5 0 0 1 .708 0l3 3a.5.5 0 0 1 0 .708l-10 10a.5.5 0 0 1-.168.11l-5 2a.5.5 0 0 1-.65-.65l2-5a.5.5 0 0 1 .11-.168l10-10zM11.207 2.5L13.5 4.793 14.793 3.5 12.5 1.207 11.207 2.5zm1.586 3L10.5 3.207 4 9.707V10h.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.5h.293l6.5-6.5zm-9.761 5.175l-.106.106-1.528 3.821 3.821-1.528.106-.106A.5.5 0 0 1 5 12.5V12h-.5a.5.5 0 0 1-.5-.5V11h-.5a.5.5 0 0 1-.468-.325z"/>
          </svg>
        </button>
        <button type="button" class="toolbar-btn" title="Copy" @click.stop="handleCopy">
          <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor">
            <path d="M4 2a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V2zm2-1a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1H6z"/>
            <path d="M2 5a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1v-1h1v1a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h1v1H2z"/>
          </svg>
        </button>
        <button type="button" class="toolbar-btn" title="Save" @click.stop="handleSave">
          <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor">
            <path d="M2 1a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1H9.5a1 1 0 0 0-1 1v7.293l2.146-2.147a.5.5 0 0 1 .708.708l-3 3a.5.5 0 0 1-.708 0l-3-3a.5.5 0 1 1 .708-.708L7.5 9.293V2a2 2 0 0 1 2-2H14a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V2a2 2 0 0 1 2-2h2.5a.5.5 0 0 1 0 1H2z"/>
          </svg>
        </button>
        <button type="button" class="toolbar-btn toolbar-btn-danger" title="Delete" @click.stop="handleDelete">
          <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor">
            <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z"/>
            <path fill-rule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4L4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"/>
          </svg>
        </button>
      </div>
    </Transition>

    <div class="element-preview">
      <div v-if="element.general.label" class="mb-2">
        <strong>{{ element.general.label }}</strong>
        <span v-if="element.general.required" class="text-danger">*</span>
      </div>

      <div v-if="element.elementType === 'text' || element.elementType === 'email' || element.elementType === 'number'">
        <div class="input-group">
          <input
            type="text"
            class="form-control"
            :placeholder="element.general.placeholder || ''"
            disabled
          />
        </div>
      </div>

      <div v-else-if="element.elementType === 'textarea'">
        <textarea
          class="form-control"
          :placeholder="element.general.placeholder || ''"
          :rows="element.rows || 3"
          disabled
        ></textarea>
      </div>

      <div v-else-if="element.elementType === 'select'">
        <select class="form-select" disabled>
          <option>{{ element.general.placeholder || 'Select an option' }}</option>
          <option v-for="option in element.general.options" :key="option.value">
            {{ option.label }}
          </option>
        </select>
      </div>

      <div v-else-if="element.elementType === 'checkbox'">
        <div v-for="option in element.general.options" :key="option.value" class="form-check">
          <input class="form-check-input" type="checkbox" disabled />
          <label class="form-check-label">{{ option.label }}</label>
        </div>
      </div>

      <div v-else-if="element.elementType === 'radio'">
        <div v-for="option in element.general.options" :key="option.value" class="form-check">
          <input class="form-check-input" type="radio" disabled />
          <label class="form-check-label">{{ option.label }}</label>
        </div>
      </div>

      <div v-else-if="element.elementType === 'date'">
        <input type="date" class="form-control" disabled />
      </div>

      <div v-else-if="element.elementType === 'file'">
        <input type="file" class="form-control" disabled />
      </div>

      <div v-else-if="element.elementType === 'todoList'" class="border rounded p-2">
        <div v-if="(element.todoList?.starterItems.length || 0) === 0" class="text-muted">No starter items</div>
        <div v-for="item in element.todoList?.starterItems" :key="item.id" class="d-flex gap-2 border-bottom py-2">
          <input class="form-check-input" type="checkbox" disabled />
          <div>
            <div>{{ item.text || 'Untitled TODO item' }}</div>
            <small v-if="item.description" class="text-muted">{{ item.description }}</small>
          </div>
        </div>
        <small class="text-muted">{{ element.todoList?.mode === 'dynamic' ? `Dynamic · ${element.todoList.maxItems === -1 ? 'Unlimited' : `Max ${element.todoList.maxItems}`}` : 'Static' }}</small>
      </div>

      <div v-else-if="element.elementType === 'heading'">
        <h3>{{ element.general.label || 'Heading Text' }}</h3>
      </div>

      <div v-else-if="element.elementType === 'paragraph'">
        <p>{{ element.content || element.general.label || 'Paragraph text goes here...' }}</p>
      </div>

      <div v-else-if="element.elementType === 'button'">
        <button type="button" class="btn btn-primary" disabled>
          {{ element.general.label || 'Button' }}
        </button>
      </div>

      <div v-if="element.general.helpText" class="form-text mt-1">
        {{ element.general.helpText }}
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import type { Element, Column } from '../../types/form-builder';
import { applyInlineStyles } from '../../utils/form-helpers';

interface Props {
  component: Element | Column;
  elementIndex: number;
  columnUid: string;
  selectedUid?: string;
}

interface Emits {
  (e: 'select', uid: string): void;
  (e: 'edit', uid: string): void;
  (e: 'update', component: Element): void;
  (e: 'delete', uid: string): void;
  (e: 'copy', element: Element): void;
  (e: 'save', element: Element): void;
  (e: 'reorder', fromIndex: number, toIndex: number): void;
  (e: 'moveToColumn', data: { element: Element; fromColumnUid: string; toIndex: number }): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

const showToolbar = ref(false);
const isDragOver = ref(false);

const element = computed(() => props.component as Element);

const isSelected = computed(() => props.selectedUid === props.component.uid);

const elementStyles = computed(() => applyInlineStyles(props.component.design));

function handleSelect() {
  emit('select', props.component.uid);
}

function handleEdit() {
  emit('edit', props.component.uid);
}

function handleCopy() {
  emit('copy', element.value);
}

function handleSave() {
  emit('save', element.value);
}

function handleDelete() {
  emit('delete', props.component.uid);
}

function handleDragStart(e: DragEvent) {
  e.stopPropagation();
  if (e.dataTransfer) {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('application/element', JSON.stringify({
      type: 'element',
      element: element.value,
      index: props.elementIndex,
      columnUid: props.columnUid
    }));
  }
}

function handleDragEnd() {
  isDragOver.value = false;
}

function handleDragOver(e: DragEvent) {
  if (e.dataTransfer?.types.includes('application/element')) {
    e.preventDefault();
    e.stopPropagation();
    isDragOver.value = true;
  }
}

function handleDragLeave() {
  isDragOver.value = false;
}

function handleDrop(e: DragEvent) {
  e.preventDefault();
  e.stopPropagation();
  isDragOver.value = false;

  const elementData = e.dataTransfer?.getData('application/element');
  if (elementData) {
    const { element: draggedElement, index: fromIndex, columnUid: fromColumnUid } = JSON.parse(elementData);
    if (fromColumnUid === props.columnUid) {
      if (fromIndex !== props.elementIndex) {
        emit('reorder', fromIndex, props.elementIndex);
      }
    } else {
      emit('moveToColumn', {
        element: draggedElement,
        fromColumnUid,
        toIndex: props.elementIndex
      });
    }
  }
}
</script>

<style scoped>
.builder-element {
  border: 2px dashed #ffc107;
  padding: 10px;
  border-radius: 6px;
  transition: all 0.2s;
  background-color: white;
  position: relative;
}

.builder-element:hover {
  border-color: #fd7e14;
}

.builder-element.selected {
  border-color: #fd7e14;
  border-style: solid;
  background-color: rgba(253, 126, 20, 0.05);
}

.builder-element.drag-over {
  border-color: #198754;
  background-color: rgba(25, 135, 84, 0.1);
}

.element-toolbar {
  position: absolute;
  top: -32px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 2px;
  background: white;
  padding: 4px;
  border-radius: 6px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  border: 1px solid #dee2e6;
  z-index: 10;
}

.toolbar-btn {
  width: 26px;
  height: 26px;
  padding: 0;
  border: 1px solid transparent;
  background: transparent;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #495057;
  transition: all 0.15s;
}

.toolbar-btn:hover {
  background-color: #e9ecef;
  border-color: #0d6efd;
  color: #0d6efd;
}

.toolbar-btn-danger:hover {
  background-color: #f8d7da;
  border-color: #dc3545;
  color: #dc3545;
}

.element-preview {
  pointer-events: none;
  opacity: 0.9;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.15s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
